import { auth } from "@/lib/firebase";
import { Button } from "@app-components";
import React from "react";
import { useTranslation } from "react-i18next";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  fallback?: React.ReactNode;
  t: (key: string) => string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const withEnhancements = (Component: typeof BasicBoundary) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function WrappedComponent(props: any) {
    const { t } = useTranslation();

    return <Component {...props} t={t} />;
  };
};

class BasicBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentDidCatch(error: any, info: any) {
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    /* logErrorToMyService(error, info.componentStack); */
    console.error(error, info);
  }

  /* componentDidUpdate(prevProps: Props) {
    // Reset the error boundary state if the location has changed
    console.log("Changed", this.props.location, prevProps.location);
    if (this.props.location !== prevProps.location) {
      this.setState({ hasError: false });
    }
  } */

  render() {
    const { fallback, t } = this.props;

    if (this.state.hasError) {
      // If a specific fallback UI is provided, return that
      // Otherwise show default fallback UI
      return (
        fallback || (
          <div style={this.props.style} className={this.props.className}>
            <div className="pageHeader">
              <h1>{t("ERROR_BOUNDARY.HEADER")}</h1>
              <p>{t("ERROR_BOUNDARY.BODY")}</p>
              <p>
                {t("ERROR_BOUNDARY.MESSAGE")}
                {": "}
                <strong>{this.state.error?.message}</strong>
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--wlcm-spacing-xs)",
                alignItems: "center",
              }}
            >
              <Button dark onClick={() => window.location.reload()}>
                {t("ERROR_BOUNDARY.RELOAD")}
              </Button>
              <p>{t("OR")}</p>
              <Button
                secondary
                onClick={() => {
                  auth?.signOut();
                  window.location.reload();
                }}
              >
                {t("SIDEBAR.LOG_OUT")}
              </Button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

const ErrorBoundary = withEnhancements(BasicBoundary);
export default ErrorBoundary;
